import {
  useFormatNumber,
  useFormatToCurrencyCutoff,
  useTranslation,
} from '@dreamstack/i18n'
import maxBy from 'lodash/maxBy'
import minBy from 'lodash/minBy'

const formatNumberOptions = {
  style: 'decimal',
  maximumFractionDigits: 1,
} as Intl.NumberFormatOptions

export const useMinMaxOfProperties = ({
  properties,
}: {
  properties: {
    number_of_rooms: number | null
    property_space_value: number | null
    price: number | null
    price_per_sqm: number | null
  }[]
}) => {
  const minMax = minMaxOfProperties({ properties })
  const formattedMinMax = useFormatMinMax(minMax)

  return {
    ...minMax,
    ...formattedMinMax,
  }
}

export const useFormatMinMax = ({
  minPriceRaw,
  minRooms,
  maxRooms,
  minSqm,
  maxSqm,
}: {
  minSqm: number
  maxSqm: number
  minPriceRaw: number
  maxPriceRaw: number
  minRooms: number
  maxRooms: number
}) => {
  const t = useTranslation()
  const formatDecimal = useFormatNumber(formatNumberOptions)
  const formatPrice = useFormatToCurrencyCutoff()

  const minPrice = formatPrice(minPriceRaw)
  const rooms = `${formatDecimal(minRooms)} - ${formatDecimal(maxRooms)}`
  const sqm = `${formatDecimal(minSqm)} - ${formatDecimal(maxSqm)} ${t(
    'accentro:squareMeter'
  )}`

  return {
    minPrice,
    rooms,
    sqm,
  }
}

export function minMaxOfProperties({
  properties,
}: {
  properties: {
    number_of_rooms: number | null
    property_space_value: number | null
    price: number | null
    price_per_sqm: number | null
  }[]
}) {
  let propertiesWithPriceOver1500 = properties.filter(
    (p) => p.price_per_sqm && p.price_per_sqm > 1500
  )

  if (propertiesWithPriceOver1500.length === 0) {
    propertiesWithPriceOver1500 = properties
  }

  const minRooms =
    minBy(propertiesWithPriceOver1500, (p) => p.number_of_rooms)
      ?.number_of_rooms || 0
  const maxRooms =
    maxBy(propertiesWithPriceOver1500, (p) => p.number_of_rooms)
      ?.number_of_rooms || 0
  const minSqm =
    minBy(propertiesWithPriceOver1500, (p) => p.property_space_value)
      ?.property_space_value || 0
  const maxSqm =
    maxBy(propertiesWithPriceOver1500, (p) => p.property_space_value)
      ?.property_space_value || 0

  const minPriceRaw =
    minBy(propertiesWithPriceOver1500, (p) => p.price)?.price || 0
  const maxPriceRaw =
    maxBy(propertiesWithPriceOver1500, (p) => p.price)?.price || 0

  return {
    minSqm,
    maxSqm,
    minPriceRaw,
    maxPriceRaw,
    minRooms,
    maxRooms,
  }
}
